import { isPointInRegion } from '../utils/geometry';
import { Terrain } from '../terrain/Terrain';
import { HeightField } from '../terrain/HeightField';
import type { Course } from '../course/Course';
import type { MaterialLibrary } from './MaterialLibrary';
import { SplatMapBaker } from './SplatMapBaker';
import { FairwayRenderer } from './FairwayRenderer';
import { GreenRenderer } from './GreenRenderer';
import { BunkerRenderer } from './BunkerRenderer';
import { computeCourseBounds } from './computeCourseBounds';
import { heightFieldFromDocument } from '../course/TerrainSculpting';

const HEIGHTFIELD_RESOLUTION = 128;
const SPLAT_RESOLUTION = 512;
const SPLAT_FEATHER_PIXELS = 0;

/**
 * Builds the Terrain (heightmap mesh + blended material + surface-type
 * sampler) for a Course. This is the one place that decides terrain size,
 * position, and resolution — all derived from computeCourseBounds(course),
 * never hardcoded — and orchestrates Fairway/Green/BunkerRenderer, which
 * each contribute their surface's splat weight and (for green/bunker) their
 * own heightfield sculpting.
 */
export class TerrainRenderer {
  private readonly fairway = new FairwayRenderer();
  private readonly green = new GreenRenderer();
  private readonly bunker = new BunkerRenderer();

  constructor(private readonly materials: MaterialLibrary) {}

  render(course: Course): Terrain {
    const bounds = computeCourseBounds(course);
    const width = bounds.maxX - bounds.minX;
    const depth = bounds.maxZ - bounds.minZ;
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerZ = (bounds.minZ + bounds.maxZ) / 2;

    const heightField = course.authoredTerrain
      ? heightFieldFromDocument(course.authoredTerrain)
      : new HeightField({
          width,
          depth,
          centerX,
          centerZ,
          resolution: HEIGHTFIELD_RESOLUTION,
          generator: designedTerrainHeight(course),
        });

    // Sculpt before the splat bake so a bunker's recessed bowl and the
    // green's flattening are both settled before anyone reads/paints from them.
    if (!course.authoredTerrain) {
      this.green.sculpt(course, heightField);
      this.bunker.sculpt(course, heightField);
    }

    const baker = new SplatMapBaker(bounds, SPLAT_RESOLUTION);
    this.fairway.paintSplat(course, baker);
    this.green.paintSplat(course, baker);
    this.bunker.paintSplat(course, baker);
    const splatTexture = baker.toTexture(SPLAT_FEATHER_PIXELS);

    const material = this.materials.terrainMaterial(splatTexture);

    const terrain = new Terrain({
      width,
      depth,
      centerX,
      centerZ,
      resolution: HEIGHTFIELD_RESOLUTION,
      heightField,
      material,
    });

    terrain.setSurfaceTypeSampler((x, z) => course.surfaceClassifier.classify(x, z).surfaceType);

    return terrain;
  }
}

/**
 * Builds a height function shaped by the course's own hole data rather than
 * pure noise: a gentle rolling base (subtle, low-amplitude layered sine —
 * present so the course doesn't feel dead flat, but deliberately understated)
 * plus a "fairway corridor" bias that sits fairway/green ground slightly
 * lower than the surrounding rough. Real course design frequently channels
 * fairways this way to help balls funnel toward the short grass instead of
 * bounding away from it — the point isn't a physically simulated funnel
 * (no ball physics here), just terrain that *reads* as intentionally
 * designed rather than randomly generated, which was the explicit ask.
 */
function designedTerrainHeight(course: Course): (x: number, z: number) => number {
  return (x, z) => {
    const rolling = gentleRollingBase(x, z);

    for (const hole of course.holes) {
      if (
        isPointInRegion({ x, z }, hole.def.fairway) ||
        isPointInRegion({ x, z }, hole.def.green)
      ) {
        return rolling - 0.6;
      }
    }
    return rolling;
  };
}

/** Subtle, low-amplitude layered sine — gentle rolling character, not "wavy" or noisy. */
function gentleRollingBase(x: number, z: number): number {
  return Math.sin(x * 0.012) * 0.6 + Math.cos(z * 0.015) * 0.5 + Math.sin((x + z) * 0.006) * 0.9;
}
